import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    type TextFieldProps,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { apiFetch } from '../lib/api';
import { PageHeader } from '../components/PageHeader';

/**
 * Outstanding Invoicing.
 *
 * Timesheet work not yet invoiced — a port of outstanding_invoicing_list_view.php:
 * Date, Client, Job Address, Staff Member, Total Hours and a Mark Invoiced button
 * per row. Added here: a financial-year filter (as on the client Invoicing tab),
 * a client picker, a running hours total, and ticking several rows to mark them
 * in one go.
 *
 * Create Invoice (legacy: the client screen's Create Invoice) starts here: tick
 * one client's entries and it opens the invoice page for them
 * (OutstandingInvoiceCreate), which sends a draft to Xero.
 *
 * Laid out like the Jobs page (click2gro General Tasks). The list is under a
 * hundred rows, so it loads whole and pages in the browser.
 */

interface OutstandingEntry {
    readonly id: number;
    readonly workDate: string;
    readonly clientId: number | null;
    readonly clientName: string | null;
    readonly jobNumber: number | null;
    readonly jobAddress: string | null;
    readonly jobSuburb: string | null;
    readonly staffName: string | null;
    readonly totalHours: number | null;
    readonly isOpen: boolean;
}

/** DD/MM/YYYY, as the legacy list showed it. The list spans years, so the year stays. */
function formatDate(iso: string): string {
    return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/** Financial year ending 30 June of `endYear`, e.g. 2027 = 1 Jul 2026 – 30 Jun 2027. */
interface Period {
    readonly key: string;
    /** Short, for the field itself, e.g. "FY 2026–27". */
    readonly label: string;
    /** The dates, shown as the field's subtext. */
    readonly range?: string;
    readonly from?: string;
    readonly to?: string;
}

function financialYear(endYear: number): Period {
    return {
        key: `fy${String(endYear)}`,
        label: `FY ${String(endYear - 1)}–${String(endYear).slice(2)}`,
        range: `1 Jul ${String(endYear - 1)} – 30 Jun ${String(endYear)}`,
        from: `${String(endYear - 1)}-07-01`,
        to: `${String(endYear)}-06-30`,
    };
}

/** The financial year containing today, in Melbourne. */
function currentFinancialYearEnd(): number {
    const [y, m] = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Australia/Melbourne',
        year: 'numeric',
        month: '2-digit',
    })
        .format(new Date())
        .split('-')
        .map(Number);
    return (m ?? 1) >= 7 ? (y ?? 0) + 1 : (y ?? 0);
}

/** Financial year (by its 30 June end) that a date falls in. */
function financialYearEndOf(iso: string): number {
    const year = Number(iso.slice(0, 4));
    return Number(iso.slice(5, 7)) >= 7 ? year + 1 : year;
}

const ALL_YEARS: Period = { key: 'all', label: 'All years' };

function inPeriod(iso: string, period: Period): boolean {
    return (period.from === undefined || iso >= period.from) && (period.to === undefined || iso <= period.to);
}

function addressOf(e: OutstandingEntry): string {
    return [e.jobAddress, e.jobSuburb].filter((p) => p !== null && p !== '').join(', ');
}

interface ClientOption {
    readonly id: number;
    readonly name: string;
    readonly count: number;
}

export function OutstandingInvoicing(): React.JSX.Element {
    const navigate = useNavigate();
    const [entries, setEntries] = useState<readonly OutstandingEntry[]>([]);
    const [clientId, setClientId] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [marking, setMarking] = useState<ReadonlySet<number>>(new Set());
    const [confirmBulk, setConfirmBulk] = useState(false);
    const [nonce, setNonce] = useState(0);
    const thisYearEnd = currentFinancialYearEnd();
    const thisYearKey = `fy${String(thisYearEnd)}`;
    const [periodKey, setPeriodKey] = useState(thisYearKey);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        apiFetch<{ entries: readonly OutstandingEntry[] }>('/outstanding-invoicing')
            .then((r) => {
                if (!cancelled) {
                    setEntries(r.entries);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load outstanding invoicing');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    // From the financial year of the oldest outstanding entry up to this year, so
    // the list reaches back exactly as far as there is unbilled work.
    const periods = useMemo<readonly Period[]>(() => {
        const oldest = entries.reduce(
            (min, e) => Math.min(min, financialYearEndOf(e.workDate)),
            thisYearEnd,
        );
        return [
            ...Array.from({ length: thisYearEnd - oldest + 1 }, (_, i) => financialYear(thisYearEnd - i)),
            ALL_YEARS,
        ];
    }, [entries, thisYearEnd]);
    const period = periods.find((p) => p.key === periodKey) ?? financialYear(thisYearEnd);

    /** Entries per year, shown in the dropdown so older unbilled work is not out of sight. */
    const countOf = (p: Period): number => entries.filter((e) => inPeriod(e.workDate, p)).length;

    const inYear = useMemo(() => entries.filter((e) => inPeriod(e.workDate, period)), [entries, period]);

    /** Clients with outstanding work this period, for the picker, with their entry counts. */
    const clientOptions = useMemo<readonly ClientOption[]>(() => {
        const byId = new Map<number, ClientOption>();
        for (const e of inYear) {
            if (e.clientId !== null) {
                const prev = byId.get(e.clientId);
                byId.set(e.clientId, { id: e.clientId, name: e.clientName ?? '—', count: (prev?.count ?? 0) + 1 });
            }
        }
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [inYear]);
    const client = clientOptions.find((o) => o.id === clientId) ?? null;

    const filtered = useMemo(
        () => (client === null ? inYear : inYear.filter((e) => e.clientId === client.id)),
        [inYear, client],
    );

    const totalHours = useMemo(() => filtered.reduce((sum, e) => sum + (e.totalHours ?? 0), 0), [filtered]);

    // Clamp rather than reset in an effect: a filter that shrinks the list
    // should land on its last page, not an empty one.
    const lastPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = filtered.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    const pageIds = pageRows.map((e) => e.id);
    const pageSelected = pageIds.filter((id) => selected.has(id)).length;

    function togglePage(): void {
        setSelected((prev) => {
            const next = new Set(prev);
            if (pageSelected === pageIds.length) {
                pageIds.forEach((id) => next.delete(id));
            } else {
                pageIds.forEach((id) => next.add(id));
            }
            return next;
        });
    }

    function toggle(id: number): void {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }

    function markInvoiced(ids: readonly number[]): void {
        setMarking(new Set(ids));
        setError(null);
        apiFetch<{ updated: number; message: string }>('/outstanding-invoicing/mark-invoiced', {
            method: 'POST',
            body: JSON.stringify({ ids }),
        })
            .then((r) => {
                setNotice(r.message);
                setSelected((prev) => {
                    const next = new Set(prev);
                    ids.forEach((id) => next.delete(id));
                    return next;
                });
                // Drop them straight away rather than waiting on the reload.
                setEntries((prev) => prev.filter((e) => !ids.includes(e.id)));
                reload();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not mark as invoiced');
            })
            .finally(() => {
                setMarking(new Set());
                setConfirmBulk(false);
            });
    }

    const busy = marking.size > 0;

    // Create Invoice takes one client's finished work. Say why not, rather than just greying it out.
    const selectedEntries = entries.filter((e) => selected.has(e.id));
    const selectedClients = new Set(selectedEntries.map((e) => e.clientId));
    const selectedHours = selectedEntries.reduce((sum, e) => sum + (e.totalHours ?? 0), 0);
    const invoiceBlocker =
        selectedEntries.length === 0
            ? 'Tick the entries to invoice'
            : selectedClients.has(null)
              ? 'An entry has no client'
              : selectedClients.size > 1
                ? 'Entries for more than one client are ticked. Pick the client first.'
                : selectedEntries.some((e) => e.isOpen || e.totalHours === null)
                  ? 'An entry is still clocked on, so it has no hours to bill'
                  : null;
    const selectedClientName = selectedClients.size === 1 ? (selectedEntries[0]?.clientName ?? null) : null;

    function createInvoice(): void {
        // Relative, so the dev preview at /preview/outstanding-invoicing opens its own copy.
        void navigate(`invoice?ids=${selectedEntries.map((e) => e.id).join(',')}`);
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Outstanding Invoicing" subtitle="Work completed and not yet invoiced" />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        useFlexGap
                        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                        flexWrap="wrap"
                        sx={{ flex: 1 }}
                    >
                        <Button
                            variant={periodKey === thisYearKey ? 'contained' : 'outlined'}
                            color={periodKey === thisYearKey ? 'primary' : 'inherit'}
                            onClick={() => {
                                setPeriodKey(thisYearKey);
                                setPage(0);
                                setSelected(new Set());
                            }}
                            sx={{ whiteSpace: 'nowrap', minHeight: 40 }}
                        >
                            This Year
                        </Button>
                        <TextField
                            select
                            size="small"
                            label="Financial year"
                            value={period.key}
                            onChange={(e) => {
                                setPeriodKey(e.target.value);
                                setPage(0);
                                // As with the client picker: never bulk-mark rows the year filter has hidden.
                                setSelected(new Set());
                            }}
                            helperText={`${period.range ?? 'Every year'} · ${String(countOf(period))} ${countOf(period) === 1 ? 'entry' : 'entries'}`}
                            slotProps={{
                                select: {
                                    renderValue: () => (period.key === thisYearKey ? `${period.label} — this year` : period.label),
                                },
                            }}
                            sx={{ minWidth: { sm: 240 } }}
                        >
                            {periods.map((p) => {
                                const n = countOf(p);
                                return (
                                    <MenuItem key={p.key} value={p.key}>
                                        <Stack>
                                            <span>{p.key === thisYearKey ? `${p.label} — this year` : p.label}</span>
                                            <Typography component="span" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {p.range ?? 'Every year'} · {n} {n === 1 ? 'entry' : 'entries'}
                                            </Typography>
                                        </Stack>
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                        <Autocomplete
                            size="small"
                            options={clientOptions}
                            value={client}
                            onChange={(_e, v) => {
                                setClientId(v?.id ?? null);
                                setPage(0);
                                // A selection hidden by the client filter should not be marked or invoiced.
                                setSelected(new Set());
                            }}
                            getOptionLabel={(o) => o.name}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderOption={(props, o) => {
                                // As in SuburbField: MUI types the key as any, and React 19 warns
                                // if it is spread. The client id is a stable key of our own.
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars
                                const { key: _muiKey, ...rest } = props;
                                return (
                                    <li key={o.id} {...rest}>
                                        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                                            <span>{o.name}</span>
                                            <Typography component="span" color="text.secondary" sx={{ ml: 2 }}>
                                                {o.count}
                                            </Typography>
                                        </Stack>
                                    </li>
                                );
                            }}
                            autoHighlight
                            openOnFocus
                            noOptionsText="No client with outstanding work"
                            renderInput={(params) => (
                                <TextField
                                    {...(params as TextFieldProps)}
                                    size="small"
                                    label="Client"
                                    placeholder="Type a client name..."
                                />
                            )}
                            sx={{ width: { xs: '100%', sm: 320 } }}
                        />
                        {/* Centred on the fields, not their subtext. */}
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ minHeight: 40 }}>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${String(filtered.length)} ${filtered.length === 1 ? 'entry' : 'entries'}`}
                            />
                            <Chip size="small" variant="outlined" label={`${totalHours.toFixed(2)} hours`} />
                            {loading ? <CircularProgress size={18} /> : null}
                        </Stack>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button
                            variant="outlined"
                            startIcon={<DoneAllIcon />}
                            disabled={selected.size === 0 || busy}
                            onClick={() => {
                                setConfirmBulk(true);
                            }}
                        >
                            {selected.size === 0 ? 'Mark Selected Invoiced' : `Mark ${String(selected.size)} Invoiced`}
                        </Button>
                        <Tooltip title={invoiceBlocker ?? ''}>
                            <span>
                                <Button
                                    variant="contained"
                                    color={invoiceBlocker === null ? 'success' : 'primary'}
                                    startIcon={<ReceiptLongIcon />}
                                    disabled={invoiceBlocker !== null || busy}
                                    onClick={createInvoice}
                                    sx={{ whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
                                >
                                    {selected.size === 0 ? 'Create Invoice' : `Create Invoice (${String(selected.size)})`}
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>

            {selected.size > 0 ? (
                <Paper
                    sx={(theme) => ({
                        p: 1.5,
                        mb: 2,
                        border: '1px solid',
                        borderColor: invoiceBlocker === null ? 'success.main' : 'warning.main',
                        bgcolor: alpha(
                            invoiceBlocker === null ? theme.palette.success.main : theme.palette.warning.main,
                            0.08,
                        ),
                    })}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>
                            {selected.size} {selected.size === 1 ? 'entry' : 'entries'} selected ·{' '}
                            {selectedHours.toFixed(2)} hours
                            {selectedClientName !== null ? ` · ${selectedClientName}` : ''}
                            {invoiceBlocker !== null ? (
                                <Typography component="span" color="warning.main" sx={{ ml: 1, fontWeight: 400 }}>
                                    — {invoiceBlocker}
                                </Typography>
                            ) : null}
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => {
                                setSelected(new Set());
                            }}
                        >
                            Clear
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ReceiptLongIcon />}
                            disabled={invoiceBlocker !== null || busy}
                            onClick={createInvoice}
                        >
                            Create Invoice
                        </Button>
                    </Stack>
                </Paper>
            ) : null}

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}
            {notice !== null ? (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setNotice(null);
                    }}
                >
                    {notice}
                </Alert>
            ) : null}

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 820 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={pageIds.length > 0 && pageSelected === pageIds.length}
                                    indeterminate={pageSelected > 0 && pageSelected < pageIds.length}
                                    disabled={pageIds.length === 0 || busy}
                                    onChange={togglePage}
                                    slotProps={{ input: { 'aria-label': 'Select all on this page' } }}
                                />
                            </TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>Job Address</TableCell>
                            <TableCell>Staff Member</TableCell>
                            <TableCell align="right">Total Hours</TableCell>
                            <TableCell align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">
                                        {loading
                                            ? 'Loading outstanding work...'
                                            : client === null
                                              ? period.key === 'all'
                                                  ? 'Nothing outstanding. All work has been invoiced.'
                                                  : 'Nothing outstanding in this financial year.'
                                              : `Nothing outstanding for ${client.name}.`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageRows.map((e, idx) => {
                                const address = addressOf(e);
                                const checked = selected.has(e.id);
                                return (
                                    <TableRow
                                        key={e.id}
                                        hover
                                        selected={checked}
                                        onClick={() => {
                                            toggle(e.id);
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent',
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox size="small" checked={checked} disabled={busy} />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(e.workDate)}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{e.clientName ?? '—'}</TableCell>
                                        <TableCell>
                                            {address === '' ? '—' : address}
                                            {e.jobNumber !== null ? (
                                                <Typography
                                                    component="span"
                                                    sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }}
                                                >
                                                    #{e.jobNumber}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>{e.staffName ?? '—'}</TableCell>
                                        <TableCell align="right">
                                            {e.isOpen ? (
                                                <Chip size="small" color="warning" variant="outlined" label="Still clocked on" />
                                            ) : e.totalHours === null ? (
                                                '—'
                                            ) : (
                                                e.totalHours.toFixed(2)
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={busy}
                                                startIcon={
                                                    marking.has(e.id) ? <CircularProgress size={14} color="inherit" /> : null
                                                }
                                                onClick={(ev) => {
                                                    ev.stopPropagation();
                                                    markInvoiced([e.id]);
                                                }}
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                Mark Invoiced
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={filtered.length}
                page={currentPage}
                onPageChange={(_e, p) => {
                    setPage(p);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(Number.parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}
            />

            <Dialog
                open={confirmBulk}
                onClose={() => {
                    setConfirmBulk(false);
                }}
                maxWidth="xs"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Mark as Invoiced
                    <IconButton
                        size="small"
                        aria-label="Close"
                        disabled={busy}
                        onClick={() => {
                            setConfirmBulk(false);
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography>
                        Mark {selected.size} {selected.size === 1 ? 'entry' : 'entries'} as invoiced? They will be
                        removed from this list.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => {
                            setConfirmBulk(false);
                        }}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={busy}
                        startIcon={busy ? <CircularProgress size={20} color="inherit" /> : <DoneAllIcon />}
                        onClick={() => {
                            markInvoiced([...selected]);
                        }}
                    >
                        {busy ? 'Saving...' : 'Mark Invoiced'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
